import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import Fragment from "sap/ui/core/Fragment";
import type Dialog from "sap/m/Dialog";
import type { ResponseError } from "../types/Errors";
import type { ListBase$ItemPressEvent } from "sap/m/ListBase";

/**
 * Statistics page — aggregate metrics pulled from License Runtime via the portal backend
 * (GET /api/v1/statistics/lr; 5-min cached; CompuTec-only, gated on orgs.read-all). Graceful:
 * when LR is unavailable the endpoint returns available=false and the view shows a banner
 * instead of empty tiles.
 */
export default class Statistics extends BaseController {
	private installDialog?: Dialog;

	public onInit(): void {
		const model = new JSONModel({ loaded: false, available: false, summary: null, installations: [], selected: null });
		this.getView()?.setModel(model, "stats");
		// Arrow wrapper zamiast (this.onRouteMatched, this) — spójnie z pozostałymi kontrolerami
		// (Dashboard, Deployments, AuditLog) i bez ostrzeżenia @typescript-eslint/unbound-method.
		this.getRouter().getRoute("statistics")?.attachPatternMatched(() => { this.onRouteMatched(); });
	}

	private onRouteMatched(): void {
		void this.loadStats();
		void this.loadInstallations();
	}

	public onRefresh(): void {
		void this.loadStats();
		void this.loadInstallations();
	}

	private async loadStats(): Promise<void> {
		const model = this.getView()?.getModel("stats") as JSONModel;
		model.setProperty("/loaded", false);
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const res = await fetcher.json<{ available: boolean; summary: unknown }>("/api/v1/statistics/lr");
			model.setProperty("/loaded", true);
			model.setProperty("/available", res.available);
			model.setProperty("/summary", res.summary ?? null);
		} catch (e) {
			const err = e as ResponseError;
			console.warn("[Portal] failed to load LR statistics:", err);
			model.setProperty("/loaded", true);
			model.setProperty("/available", false);
			model.setProperty("/summary", null);
		}
	}

	private async loadInstallations(): Promise<void> {
		const model = this.getView()?.getModel("stats") as JSONModel;
		try {
			const fetcher = this.getOwnerComponent().getAuthFetch();
			const list = await fetcher.json<unknown[]>("/api/v1/statistics/lr/anonymous-installations");
			model.setProperty("/installations", Array.isArray(list) ? list : []);
		} catch {
			model.setProperty("/installations", []);
		}
	}

	/** Row press → show the installation's full snapshot (incl. module list) in a dialog. */
	public async onInstallationPress(event: ListBase$ItemPressEvent): Promise<void> {
		// itemPress is fired by the table, so the pressed row comes in the listItem parameter, not as the source.
		const ctx = event.getParameter("listItem")?.getBindingContext("stats");
		if (!ctx) {
			return;
		}
		const model = this.getView()?.getModel("stats") as JSONModel;
		model.setProperty("/selected", ctx.getObject());
		if (!this.installDialog) {
			this.installDialog = await Fragment.load({
				id: this.getView()?.getId(),
				name: "computec.b2b.portal.view.AnonymousInstallationDialog",
				controller: this
			}) as Dialog;
			this.getView()?.addDependent(this.installDialog);
		}
		this.installDialog.open();
	}

	public onCloseInstallationDialog(): void {
		this.installDialog?.close();
	}
}
